'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, Square } from 'lucide-react';
import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from './_common/focusRing';

// Define the style variants using CVA
const checkboxVariants = cva(
  cn(
    focusRings.focus.visible,
    'peer size-5 shrink-0 rounded border border-border-default disabled:cursor-not-allowed disabled:opacity-50',
    'hocus:border-bg-inset'
  ),
  {
    variants: {
      variant: {
        primary:
          'inset-shadow-xs data-[state=checked]:bg-accent-emphasis data-[state=checked]:text-fg-default data-[state=checked]:border-accent-emphasis',
        primaryAlt: [
          'text-white aria-disabled:bg-brand-900/50 data-[state=checked]:bg-brand-900 data-[state=checked]:text-white data-[state=checked]:border-brand-900',
          'hover:border-border-emphasis! data-[state=checked]:border-transparent! data-[state=checked]:hover:bg-brand-800!'
        ]
      }
    },
    defaultVariants: {
      variant: 'primary'
    }
  }
);

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  testId?: string;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ variant }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      forceMount
      className={cn(
        'flex items-center justify-center text-current transition-all duration-100 size-full',
        'opacity-0 data-[state=checked]:opacity-100',
        'transform scale-0 data-[state=checked]:scale-100',
        'data-[state=indeterminate]:opacity-100',
        'data-[state=indeterminate]:scale-100',
        'data-[state=checked]:**:data-[element="indeterminate"]:hidden',
        'data-[state=indeterminate]:**:data-[element="checkmark"]:hidden'
      )}
      data-test-id={props.testId}
    >
      {props.checked === 'indeterminate' ? (
        <Square
          data-element="indeterminate"
          className="size-full scale-75 fill-fg-default"
        />
      ) : (
        <Check data-element="checkmark" className="size-full stroke-3" />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
